<template>
  <div class="imp-wrap">
    <p class="imp-intro">
      Intră în aplicație „ca" un utilizator, pe o firmă a lui, ca să vezi exact ce vede el
      (aceleași drepturi și meniu). Nu ai nevoie de parola lui. Ieși oricând din banda de sus.
      Fiecare impersonare este jurnalizată.
    </p>

    <div class="imp-layout">
      <!-- Utilizatori -->
      <aside class="imp-list">
        <input v-model="search" class="imp-search" placeholder="Caută utilizator..." />
        <div v-if="loading" class="imp-muted">Se încarcă...</div>
        <button v-for="u in filtered" :key="u.ID" class="imp-user"
          :class="{ active: selected && selected.ID === u.ID }" @click="select(u)">
          <span class="imp-u-name">{{ u.Username }}</span>
          <span v-if="u.SuperAdmin" class="imp-u-badge">admin</span>
          <span v-if="!u.Active" class="imp-u-badge off">inactiv</span>
        </button>
        <div v-if="!loading && !filtered.length" class="imp-muted">Niciun utilizator.</div>
      </aside>

      <!-- Detaliu + firme -->
      <section v-if="selected" class="imp-detail">
        <div class="imp-head">
          <div>
            <div class="imp-h-name">{{ selected.Username }}</div>
            <div class="imp-h-mail">{{ selected.Email || '—' }}</div>
          </div>
        </div>

        <div class="imp-comp-head">Alege firma pe care intri ca acest utilizator:</div>
        <div v-if="loadingComp" class="imp-muted">Se încarcă firmele...</div>
        <button v-for="c in companies" :key="companyId(c)" class="imp-comp"
          :disabled="busy" @click="impersonate(c)">
          <v-icon size="18" color="#ff2c21">mdi-login-variant</v-icon>
          <span>Intră ca <strong>{{ selected.Username }}</strong> @ {{ c.Name }}</span>
        </button>
        <div v-if="!loadingComp && !companies.length" class="imp-muted">
          Utilizatorul nu are firme asociate.
        </div>
      </section>

      <section v-else class="imp-detail imp-empty">
        <v-icon size="48" color="#cbd5e1">mdi-account-switch-outline</v-icon>
        <p>Selectează un utilizator.</p>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import notify from "devextreme/ui/notify";
import UserAdminService from "@/services/AUTH/UserAdmin";

export default defineComponent({
  name: "ImpersonateGrid",
  data() {
    return {
      users: [] as any[],
      selected: null as any,
      companies: [] as any[],
      search: "",
      loading: false,
      loadingComp: false,
      busy: false,
    };
  },
  computed: {
    filtered(): any[] {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.users;
      return this.users.filter(
        (u) => (u.Username || "").toLowerCase().includes(q) || (u.Email || "").toLowerCase().includes(q)
      );
    },
  },
  created() {
    this.load();
  },
  methods: {
    companyId(c: any): string {
      return c.ID || c.Id || c.id;
    },
    async load() {
      this.loading = true;
      try {
        const r = await UserAdminService.getAll();
        this.users = r.data || [];
      } catch {
        notify("Nu am putut încărca utilizatorii.", "error", 3000);
      } finally {
        this.loading = false;
      }
    },
    async select(u: any) {
      this.selected = u;
      this.companies = [];
      this.loadingComp = true;
      try {
        const r = await UserAdminService.getCompanies(u.Username);
        this.companies = r.data || [];
      } catch {
        notify("Nu am putut încărca firmele utilizatorului.", "error", 3000);
      } finally {
        this.loadingComp = false;
      }
    },
    async impersonate(c: any) {
      if (!this.selected) return;
      const cid = this.companyId(c);
      this.busy = true;
      try {
        const r = await UserAdminService.impersonate(this.selected.Username, cid);
        const token = r.data?.token;
        if (!token) throw new Error("Token lipsă.");

        // salvează sesiunea de SuperAdmin ca să te poți întoarce
        localStorage.setItem("ERA_ImpersonateBackup", JSON.stringify({
          jwt: localStorage.getItem("ERA_Jwt"),
          company: localStorage.getItem("ERA_Company"),
          superChoice: localStorage.getItem("ERA_SuperAdminChoice"),
        }));

        localStorage.setItem("ERA_Jwt", token);
        if (r.data?.company) localStorage.setItem("ERA_Company", JSON.stringify(r.data.company));
        localStorage.setItem("ERA_SuperAdminChoice", "false");

        window.location.assign("/"); // intră în aplicație ca utilizatorul țintă
      } catch (e: any) {
        notify(e?.response?.data?.Message || e?.message || "Eroare la impersonare.", "error", 3500);
      } finally {
        this.busy = false;
      }
    },
  },
});
</script>

<style scoped>
.imp-wrap { display: flex; flex-direction: column; height: 100%; }
.imp-intro { color: #64748b; font-size: 13px; margin-bottom: 14px; }
.imp-layout { display: flex; gap: 16px; flex: 1; min-height: 0; }
.imp-list { width: 280px; flex-shrink: 0; border: 1px solid #e5e7eb; border-radius: 12px; padding: 8px; overflow: auto; }
.imp-search { width: 100%; border: 1px solid #e2e8f0; border-radius: 9px; padding: 8px 12px; font-size: 14px; margin-bottom: 8px; outline: none; }
.imp-user { width: 100%; display: flex; align-items: center; gap: 8px; padding: 9px 12px; border: 1px solid transparent; border-radius: 9px; background: #f8fafc; margin-bottom: 6px; cursor: pointer; text-align: left; }
.imp-user.active { border-color: #ff2c21; background: #fff5f4; }
.imp-u-name { font-weight: 600; color: #1e293b; font-size: 14px; flex: 1; }
.imp-u-badge { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #1d4ed8; background: #eff6ff; padding: 2px 8px; border-radius: 20px; }
.imp-u-badge.off { color: #92400e; background: #fef3c7; }
.imp-detail { flex: 1; min-width: 0; overflow: auto; border: 1px solid #e5e7eb; border-radius: 12px; padding: 18px; }
.imp-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; color: #94a3b8; gap: 10px; }
.imp-head { margin-bottom: 16px; }
.imp-h-name { font-size: 18px; font-weight: 700; color: #1e293b; }
.imp-h-mail { font-size: 13px; color: #94a3b8; }
.imp-comp-head { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 10px; }
.imp-comp { width: 100%; display: flex; align-items: center; gap: 10px; padding: 12px 14px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; margin-bottom: 8px; cursor: pointer; font-size: 14px; color: #334155; text-align: left; }
.imp-comp:hover { border-color: #ff2c21; background: #fff5f4; }
.imp-comp:disabled { opacity: 0.6; cursor: default; }
.imp-muted { color: #94a3b8; font-size: 13px; padding: 8px 4px; }
</style>
